import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { http } from '@/lib/api';
import ContextualHelp from '@/components/ContextualHelp';
import {
  Brain, Wallet, Heart, Users, Briefcase, Activity, Sparkles, HandHeart, ArrowRight, Baby,
  Search, SlidersHorizontal, Star, CheckCircle2, Circle, PlayCircle, TrendingUp, User,
  Trophy, Share2, X, Bookmark,
} from 'lucide-react';

const LEARN_HELP = {
  whatIs: "Brotherhood University is your library of evidence-based lessons — written by licensed clinicians and organized into a catalog you can filter by topic, length, and difficulty.",
  whyMatters: "Understanding comes before change. A short lesson today gives you language for what you're feeling tomorrow. Cumulative learning outperforms binge-reading every time.",
  howToUse: "Start with the lesson Home recommends. Or search by what's on your mind (stress, fatherhood, anger, grief). Every lesson is under 15 minutes and includes practice you can carry into your day.",
  tips: [
    "Star lessons that hit — they'll surface in your Brotherhood University dashboard for a rewatch.",
    "Pair lessons with a check-in and a journal entry to make new ideas stick.",
    "Growth Plans stitch multiple lessons into a multi-week arc — try one when you're ready.",
  ],
};

const ICONS = {
  brain: Brain, wallet: Wallet, heart: Heart, users: Users, briefcase: Briefcase,
  activity: Activity, sparkles: Sparkles, 'hand-heart': HandHeart, baby: Baby,
  'trending-up': TrendingUp, user: User,
};

const CATEGORY_ICON = {
  mental_health: Brain,
  financial: Wallet,
  relationships: Heart,
  fatherhood: Baby,
  career: Briefcase,
  physical: Activity,
  faith: HandHeart,
  emotional_intelligence: Brain,
  leadership: TrendingUp,
  identity: User,
  community: Users,
  masculinity: User,
  sexual_health: Heart,
  digital_wellness: Activity,
  aging_legacy: Sparkles,
  entrepreneurship: TrendingUp,
  recovery: Activity,
  grief: Heart,
  justice: Users,
  habits: TrendingUp,
};

const FEATURED_COURSES = [
  { key: 'mental_health', title: 'Mental Health', tagline: 'Clinically informed', blurb: 'Anxiety, stress, burnout, therapy.' },
  { key: 'financial', title: 'Financial Wellness', tagline: 'Advisor guided', blurb: 'Budgeting, credit, debt, wealth.' },
  { key: 'relationships', title: 'Relationships', tagline: 'Gottman-informed', blurb: 'Communication, repair, intimacy.' },
  { key: 'fatherhood', title: 'Fatherhood', tagline: 'For Black + Brown fathers', blurb: 'Presence, cycles, healthy kids.' },
  { key: 'career', title: 'Career', tagline: 'Career-coach developed', blurb: 'Interviews, negotiation, presence.' },
  { key: 'physical', title: 'Physical Wellness', tagline: 'Evidence-based', blurb: 'Sleep, movement, screenings.' },
  { key: 'faith', title: 'Faith & Purpose', tagline: 'Reflective + practical', blurb: 'Hope, purpose, forgiveness.' },
  { key: 'emotional_intelligence', title: 'Emotional Intelligence', tagline: 'The game', blurb: 'Name it, feel it, use it.' },
  { key: 'leadership', title: 'Leadership', tagline: 'Lead yourself first', blurb: 'Vision, delegation, hard calls.' },
  { key: 'identity', title: 'Identity', tagline: 'Who you actually are', blurb: 'Race, masculinity, shame, legacy.' },
  { key: 'community', title: 'Community', tagline: 'You need people', blurb: 'Friendship, groups, brotherhood.' },
  { key: 'masculinity', title: 'Redefining Manhood', tagline: 'Masculinity, honestly', blurb: 'Strength, emotion, friendship, fatherhood.' },
  { key: 'sexual_health', title: 'Sexual Health & Intimacy', tagline: 'Shame-free & clinical', blurb: 'Body, desire, intimacy, consent.' },
  { key: 'digital_wellness', title: 'Digital Wellness', tagline: 'Reclaim your attention', blurb: 'Scrolling, comparison, boundaries, presence.' },
  { key: 'aging_legacy', title: 'Aging & Legacy', tagline: 'Grow older on purpose', blurb: 'Midlife, the body, mortality, legacy.' },
  { key: 'entrepreneurship', title: 'Building Wealth & Business', tagline: 'From employee to owner', blurb: 'First dollar, money, risk, wealth.' },
  { key: 'recovery', title: 'Addiction & Recovery', tagline: 'Shame-free & evidence-based', blurb: 'Alcohol, substances, triggers, rebuilding.' },
  { key: 'grief', title: 'Grief & Loss', tagline: 'The grief no one taught us', blurb: 'Loss, mourning, healing, honoring.' },
  { key: 'justice', title: 'Justice & Reentry', tagline: 'Know your rights', blurb: 'Rights, the system, reentry, rebuilding.' },
  { key: 'habits', title: 'Habits & Discipline', tagline: 'Systems over willpower', blurb: 'Science, triggers, discipline, consistency.' },
];

// Mood/Need chips — pattern-match against lesson.title + summary + category
const MOOD_MAP = {
  Anxiety:        ['anxiety','panic','worry','overwhelm'],
  Stress:         ['stress','pressure','burnout'],
  Anger:          ['anger','rage','patience','regulation'],
  Grief:          ['grief','loss','forgive'],
  Relationships:  ['relationship','marriage','partner','conflict','communication','intimacy'],
  Fatherhood:     ['father','parenting','son','daughter','dad','child'],
  'Money':        ['money','financial','budget','credit','debt','invest','wealth'],
  'Sleep':        ['sleep'],
  'Purpose':      ['purpose','meaning','faith','values','legacy'],
  'Confidence':   ['confidence','identity','self','masculinity'],
  'Leadership':   ['leadership','lead','delegate','vision','decision','feedback'],
  'Community':    ['community','friend','group','brotherhood','neighbor','belong'],
  'Career':       ['career','interview','negotiat','executive','workplace','network'],
};

const DIFFICULTY_FROM_DURATION = (d) => {
  const m = parseInt((d || '').match(/\d+/)?.[0] || '5', 10);
  if (m <= 4) return 'Beginner';
  if (m <= 6) return 'Intermediate';
  return 'Advanced';
};

const PATH_TIERS = {
  1: { title: 'Foundations', blurb: 'Start here — the core fundamentals.' },
  2: { title: 'Growth', blurb: 'Build on the basics and go further.' },
  3: { title: 'Mastery', blurb: 'Deepen the work and lead the way.' },
};

function MiniRing({ pct, dark }) {
  const r = 15;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-9 h-9 lg:w-10 lg:h-10" data-testid="course-ring">
      <svg viewBox="0 0 34 34" className="w-full h-full -rotate-90">
        <circle cx="17" cy="17" r={r} fill="none" stroke={dark ? 'rgba(255,255,255,0.20)' : 'rgba(11,25,44,0.10)'} strokeWidth="3" />
        <circle cx="17" cy="17" r={r} fill="none" stroke="#C5A059" strokeWidth="3" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} style={{ transition: 'stroke-dashoffset 500ms ease' }} />
      </svg>
      <div className={`absolute inset-0 flex items-center justify-center text-[9px] font-bold ${dark ? 'text-white' : 'text-navy'}`}>{pct}%</div>
    </div>
  );
}

function PathRing({ tier, pct }) {
  const r = 20;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-11 h-11 lg:w-12 lg:h-12 shrink-0" data-testid={`path-ring-${tier}`}>
      <svg viewBox="0 0 44 44" className="w-full h-full -rotate-90">
        <circle cx="22" cy="22" r={r} fill="none" stroke="rgba(11,25,44,0.16)" strokeWidth="4.5" />
        <circle
          cx="22" cy="22" r={r} fill="none" stroke="#0B192C" strokeWidth="4.5" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c}
          style={{ transition: 'stroke-dashoffset 500ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-heading text-base lg:text-lg text-navy">{tier}</div>
    </div>
  );
}

function LessonCard({ l, cat, Icon, completed, inProgress, favorited, onFav }) {
  const status = completed ? 'Completed' : inProgress ? 'In progress' : null;
  return (
    <div className="relative">
      <Link
        to={`/app/learn/lesson/${l.id}`}
        data-testid={`lesson-${l.id}`}
        className="block bg-white rounded-3xl card-soft hover:-translate-y-0.5 transition-smooth flex flex-col h-full lg:min-h-[240px] overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-5 lg:px-6 lg:pt-6">
          <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-2xl bg-beige flex items-center justify-center">
            <Icon className="text-navy" size={18} strokeWidth={1.5}/>
          </div>
          {status && (
            <span className={`text-[9px] lg:text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${completed ? 'bg-green/15 text-green' : 'bg-navy/10 text-navy'}`}>
              {status}
            </span>
          )}
        </div>
        <div className="flex flex-col flex-1 p-5 lg:p-6 pt-3 lg:pt-4">
          <p className="text-[10px] lg:text-xs uppercase tracking-widest text-navy/50 mb-1">{cat?.name || l.category}</p>
          <p className="font-heading text-base lg:text-lg text-navy leading-snug mb-2 line-clamp-2">{l.title}</p>
          <p className="text-xs lg:text-sm text-navy/60 leading-relaxed line-clamp-3 flex-1">{l.summary}</p>
          <div className="flex items-center justify-between mt-3">
            <p className="text-[10px] lg:text-xs text-navy/40 uppercase tracking-widest">{l.duration} · {DIFFICULTY_FROM_DURATION(l.duration)}</p>
          </div>
        </div>
      </Link>
      <button
        onClick={(e)=>{ e.preventDefault(); onFav(l.id); }}
        data-testid={`fav-${l.id}`}
        aria-label={favorited ? 'Unfavorite' : 'Favorite'}
        className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center card-soft hover:scale-110 transition-smooth">
        <Star size={14} className={favorited ? 'text-gold fill-gold' : 'text-navy/30'} strokeWidth={2}/>
      </button>
    </div>
  );
}

function CourseTile({ c, lessons, completedIds, Icon, saved, onToggleSave, onOpen }) {
  const cover = c.cover_image;
  const courseLessons = lessons.filter(l => l.category === c.id);
  const liveCount = courseLessons.length;
  const doneCount = courseLessons.filter(l => completedIds.has(l.id)).length;
  const pct = liveCount ? Math.round((doneCount / liveCount) * 100) : 0;
  return (
    <div className="relative">
      <button
        onClick={onOpen}
        data-testid={`course-tile-${c.id}`}
        className={`relative overflow-hidden text-left rounded-3xl transition-smooth card-soft flex flex-col h-full lg:min-h-[220px] w-full ${cover ? 'text-white ring-1 ring-white/10 hover:-translate-y-0.5' : 'bg-beige hover:bg-[#EAE5D9] text-navy'}`}>
        {cover && (
          <>
            <img src={cover} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/55 to-navy/25" />
          </>
        )}
        <div className="relative z-10 flex flex-col h-full p-5 lg:p-7">
          {doneCount > 0 && (
            <div className="absolute top-0 right-0" data-testid={`course-progress-${c.id}`}>
              <MiniRing pct={pct} dark={true} />
            </div>
          )}
          <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center mb-3 lg:mb-4 ${cover ? 'bg-white/15 backdrop-blur-md' : 'bg-white/70'}`}>
            <Icon size={18} className={cover ? 'text-white' : 'text-navy'} strokeWidth={1.5}/>
          </div>
          <p className="font-heading text-lg lg:text-xl mt-1">{c.name}</p>
          <p className={`text-xs lg:text-sm mt-1 leading-relaxed line-clamp-2 lg:line-clamp-3 flex-1 ${cover ? 'text-white/75' : 'text-navy/60'}`}>{c.description}</p>
          <p className={`text-[11px] lg:text-xs mt-3 uppercase tracking-widest ${cover ? 'text-white/60' : 'text-navy/50'}`}>{doneCount > 0 ? `${doneCount} of ${liveCount} done` : `${liveCount} lessons`} ↗</p>
        </div>
      </button>
      <button
        onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); onToggleSave(c.id); }}
        data-testid={`course-fav-${c.id}`}
        aria-label={saved ? 'Remove from saved courses' : 'Save course'}
        className="absolute top-4 left-4 z-20 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center card-soft hover:scale-110 transition-smooth">
        <Bookmark size={14} className={saved ? 'text-gold fill-gold' : 'text-navy/40'} strokeWidth={2}/>
      </button>
    </div>
  );
}

export default function Learn() {
  const { t } = useTranslation();
  const [cats, setCats] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [progressList, setProgressList] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [savedCourses, setSavedCourses] = useState([]);
  const [resume, setResume] = useState(null);
  const [recommended, setRecommended] = useState([]);

  // Filter state
  const [active, setActive] = useState('all');
  const [query, setQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [lengthFilter, setLengthFilter] = useState('all'); // all|short|medium|long
  const [statusFilter, setStatusFilter] = useState('all'); // all|not_started|in_progress|completed
  const [moodFilters, setMoodFilters] = useState([]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [savedCoursesOnly, setSavedCoursesOnly] = useState(false);
  const [sortBy, setSortBy] = useState('Recommended');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    http.get('/learn/categories').then(r => setCats(r.data));
    http.get('/learn/lessons').then(r => setLessons(r.data));
    http.get('/learn/completions').then(r => setCompletions(r.data)).catch(()=>{});
    http.get('/learn/progress').then(r => setProgressList(r.data)).catch(()=>{});
    http.get('/learn/favorites').then(r => setFavorites(r.data)).catch(()=>{});
    http.get('/learn/courses/favorites').then(r => setSavedCourses(r.data)).catch(()=>{});
    http.get('/learn/resume').then(r => setResume(r.data)).catch(()=>{});
    http.get('/learn/recommended').then(r => setRecommended(r.data)).catch(()=>{});
  }, []);

  const completedIds = useMemo(() => new Set(completions.map(c => c.lesson_id)), [completions]);
  const progressMap  = useMemo(() => {
    const m = {}; progressList.forEach(p => { m[p.lesson_id] = p; }); return m;
  }, [progressList]);
  const favoriteIds  = useMemo(() => new Set(favorites.map(f => f.lesson_id)), [favorites]);
  const savedCourseIds = useMemo(() => new Set(savedCourses.map(f => f.course_id)), [savedCourses]);
  const toggleCourseFav = async (id) => {
    // optimistic toggle
    const has = savedCourseIds.has(id);
    setSavedCourses(prev => has ? prev.filter(x => x.course_id !== id) : [...prev, { course_id: id }]);
    try {
      const r = await http.post(`/learn/courses/${id}/favorite`);
      if (r.data.favorited !== !has) {
        const nr = await http.get('/learn/courses/favorites'); setSavedCourses(nr.data);
      }
    } catch (err) {
      setSavedCourses(prev => has ? [...prev, { course_id: id }] : prev.filter(x => x.course_id !== id));
      console.warn('[learn] course favorite toggle failed:', err);
    }
  };

  // Path Complete Badge — celebrate when a member finishes an entire learning path.
  const [celebration, setCelebration] = useState(null);
  const celebratedRef = useRef(null);
  useEffect(() => {
    if (!lessons.length) return;
    if (celebratedRef.current === null) {
      try { celebratedRef.current = new Set(JSON.parse(localStorage.getItem('bh_celebrated_paths') || '[]')); }
      catch { celebratedRef.current = new Set(); }
    }
    const groups = {};
    lessons.forEach(l => { (groups[l.category] = groups[l.category] || []).push(l); });
    const completeKeys = [];
    Object.entries(groups).forEach(([cat, items]) => {
      [1, 2, 3].forEach(tier => {
        const tl = items.filter(l => (l.path || 1) === tier);
        if (tl.length && tl.every(l => completedIds.has(l.id))) completeKeys.push(`${cat}:${tier}`);
      });
    });
    // First run with this feature: mark already-complete paths as seen (no spam).
    if (!localStorage.getItem('bh_celebrated_paths')) {
      celebratedRef.current = new Set(completeKeys);
      localStorage.setItem('bh_celebrated_paths', JSON.stringify(completeKeys));
      return;
    }
    const fresh = completeKeys.find(k => !celebratedRef.current.has(k));
    if (fresh) {
      completeKeys.forEach(k => celebratedRef.current.add(k));
      localStorage.setItem('bh_celebrated_paths', JSON.stringify(Array.from(celebratedRef.current)));
      const [cat, tier] = fresh.split(':');
      const courseName = (cats.find(c => c.id === cat)?.name) || cat;
      setCelebration({ cat, tier: Number(tier), label: PATH_TIERS[Number(tier)].title, courseName });
    }
  }, [lessons, completedIds, cats]);

  const shareBadge = async () => {
    if (!celebration) return;
    const text = `I just completed the ${celebration.label} path in ${celebration.courseName} on Brotherhood+.`;
    try {
      if (navigator.share) { await navigator.share({ title: 'Path Complete — Brotherhood+', text }); }
      else { await navigator.clipboard.writeText(text); toast.success('Badge copied — share it with your brothers'); }
    } catch { /* share dismissed */ }
  };

  const toggleMood = (m) => setMoodFilters(prev => prev.includes(m) ? prev.filter(x=>x!==m) : [...prev, m]);
  const toggleFav = async (id) => {
    try {
      const r = await http.post(`/learn/lessons/${id}/favorite`);
      const set = new Set(favoriteIds);
      if (r.data.favorited) set.add(id); else set.delete(id);
      setFavorites(Array.from(set).map(x => ({ lesson_id: x })));
    } catch (err) {
      // Favoriting is best-effort; log for observability but don't surface.
      console.warn('[learn] favorite toggle failed:', err);
    }
  };

  const filtered = useMemo(() => {
    let list = [...lessons];
    if (active !== 'all') list = list.filter(l => l.category === active);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(l => {
        const catName = (cats.find(c => c.id === l.category)?.name || '').toLowerCase();
        return l.title.toLowerCase().includes(q) ||
          (l.summary || '').toLowerCase().includes(q) ||
          (l.category || '').toLowerCase().includes(q) ||
          catName.includes(q);
      });
    }
    if (difficultyFilter !== 'all') list = list.filter(l => DIFFICULTY_FROM_DURATION(l.duration) === difficultyFilter);
    if (lengthFilter !== 'all') {
      list = list.filter(l => {
        const min = parseInt((l.duration || '').match(/\d+/)?.[0] || '5', 10);
        if (lengthFilter === 'short') return min <= 5;
        if (lengthFilter === 'medium') return min > 5 && min <= 8;
        return min > 8;
      });
    }
    if (statusFilter === 'completed') list = list.filter(l => completedIds.has(l.id));
    if (statusFilter === 'in_progress') list = list.filter(l => progressMap[l.id] && !completedIds.has(l.id));
    if (statusFilter === 'not_started') list = list.filter(l => !completedIds.has(l.id) && !progressMap[l.id]);
    if (moodFilters.length > 0) {
      list = list.filter(l => {
        const hay = `${l.title} ${l.summary} ${l.category}`.toLowerCase();
        return moodFilters.some(m => (MOOD_MAP[m] || []).some(kw => hay.includes(kw)));
      });
    }
    if (favoritesOnly) list = list.filter(l => favoriteIds.has(l.id));

    // Sort
    if (sortBy === 'A–Z') list.sort((a,b) => a.title.localeCompare(b.title));
    else if (sortBy === 'Newest') list.sort((a,b) => (b.created_at || '').localeCompare(a.created_at || ''));
    else if (sortBy === 'Most Popular') list.sort((a,b) => (b.created_at || '').localeCompare(a.created_at || ''));
    else {
      // Recommended: pinned recommended + in-progress first, then by category grouping
      const recIds = new Set(recommended.map(r => r.id));
      list.sort((a,b) => {
        const aScore = (recIds.has(a.id) ? 2 : 0) + (progressMap[a.id] && !completedIds.has(a.id) ? 3 : 0);
        const bScore = (recIds.has(b.id) ? 2 : 0) + (progressMap[b.id] && !completedIds.has(b.id) ? 3 : 0);
        if (aScore !== bScore) return bScore - aScore;
        return (a.category || '').localeCompare(b.category || '');
      });
    }
    return list;
  }, [lessons, active, query, difficultyFilter, lengthFilter, statusFilter, moodFilters, favoritesOnly, sortBy, completedIds, progressMap, favoriteIds, recommended, cats]);

  const stats = useMemo(() => ({
    total: lessons.length,
    completed: completedIds.size,
    inProgress: progressList.filter(p => !completedIds.has(p.lesson_id) && (p.percent||0) < 100).length,
    saved: favoriteIds.size,
    completionPct: lessons.length ? Math.round((completedIds.size / lessons.length) * 100) : 0,
  }), [lessons.length, completedIds, progressList, favoriteIds]);

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-8 lg:space-y-10" data-testid="learn-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{t('learn.eyebrow')}</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-1 lg:mt-2 flex items-start gap-2 flex-wrap">{t('learn.hero')}<ContextualHelp pageTitle="Brotherhood University" content={LEARN_HELP} testid="learn-help"/></h1>
        <p className="text-navy/60 text-sm lg:text-base mt-2 lg:mt-3 lg:max-w-2xl">{t('learn.subtitle')}</p>
      </div>

      {/* Continue where I left off */}
      {resume?.lesson && (
        <Link to={`/app/learn/lesson/${resume.lesson.id}`} data-testid="learn-resume-card"
          className="block bg-navy text-white rounded-3xl p-6 lg:p-8 card-soft">
          <div className="lg:grid lg:grid-cols-4 lg:items-center lg:gap-6">
            <div className="lg:col-span-3">
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold inline-flex items-center gap-2"><PlayCircle size={12}/> {t('learn.continue_where')}</p>
              <p className="font-heading text-xl lg:text-2xl mt-2">{resume.lesson.title}</p>
              <p className="text-sm text-white/70 mt-1">{resume.lesson.summary}</p>
              <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden max-w-md">
                <div className="h-full bg-gold" style={{ width: `${resume.progress?.percent || 0}%` }}/>
              </div>
              <p className="text-xs text-white/50 mt-2">{resume.progress?.percent || 0}% · {resume.lesson.duration}</p>
              {resume.path_total > 0 && (
                <p className="text-[11px] text-gold/90 mt-1.5 inline-flex items-center gap-1.5" data-testid="learn-resume-path">
                  <TrendingUp size={11}/> Path {resume.path} · {resume.path_label} — {resume.path_completed} of {resume.path_total} lessons done
                </p>
              )}
            </div>
            <span className="hidden lg:inline-flex items-center gap-1 text-gold text-sm lg:justify-self-end">{t('learn.resume')} <ArrowRight size={14}/></span>
          </div>
        </Link>
      )}

      {/* Personal stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5" data-testid="learn-stats">
        <div className="bg-white rounded-3xl p-4 lg:p-5 card-soft">
          <p className="text-xs uppercase tracking-widest text-navy/50">{t('learn.completed')}</p>
          <p className="font-heading text-2xl lg:text-3xl mt-1">{stats.completed}<span className="text-sm lg:text-base text-navy/40"> / {stats.total}</span></p>
        </div>
        <button
          type="button"
          data-testid="learn-stat-in-progress"
          onClick={() => { setActive('all'); setStatusFilter('in_progress'); setTimeout(() => document.querySelector('[data-testid="lessons-grid"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); }}
          className="text-left bg-white rounded-3xl p-4 lg:p-5 card-soft transition-smooth hover:-translate-y-0.5 hover:ring-2 hover:ring-gold/40 focus:outline-none focus:ring-2 focus:ring-gold/60"
        >
          <p className="text-xs uppercase tracking-widest text-navy/50 inline-flex items-center gap-1">{t('learn.in_progress')} {stats.inProgress > 0 && <ArrowRight size={11} className="text-gold"/>}</p>
          <p className="font-heading text-2xl lg:text-3xl mt-1">{stats.inProgress}</p>
        </button>
        <div className="bg-beige rounded-3xl p-4 lg:p-5">
          <p className="text-xs uppercase tracking-widest text-navy/50">{t('learn.saved')}</p>
          <p className="font-heading text-2xl lg:text-3xl mt-1">{stats.saved}</p>
        </div>
        <div className="bg-navy text-white rounded-3xl p-4 lg:p-5 card-soft">
          <p className="text-xs uppercase tracking-widest text-gold">{t('learn.library_progress')}</p>
          <p className="font-heading text-2xl lg:text-3xl mt-1">{stats.completionPct}%</p>
        </div>
      </div>

      <Link to="/app/support" data-testid="learn-supporting-link" className="block bg-white rounded-3xl p-6 lg:p-8 card-soft">
        <div className="lg:grid lg:grid-cols-3 lg:items-center lg:gap-8">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{t('learn.for_loved_ones')}</p>
            <p className="font-heading text-xl lg:text-2xl mt-2">{t('learn.support_men_title')}</p>
            <p className="text-sm text-navy/60 mt-1">{t('learn.support_men_desc')}</p>
          </div>
          <span className="inline-flex items-center gap-1 text-navy text-sm lg:justify-self-end mt-3 lg:mt-0">{t('learn.open')} <ArrowRight size={14}/></span>
        </div>
      </Link>

      {/* Attachment Styles Assessment entry lives inside the rel_attachment lesson,
          not on the Brotherhood University dashboard, per user request. */}

      {/* Recommended for you — a single, tailored pick */}
      {recommended.length > 0 && (() => {
        const l = recommended[0];
        const cat = cats.find(c => c.id === l.category);
        const Icon = ICONS[cat?.icon] || CATEGORY_ICON[l.category] || Sparkles;
        const cover = l.cover_image || cat?.cover_image;
        return (
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-navy/50 font-semibold mb-3 lg:mb-5 inline-flex items-center gap-2">
              <Sparkles size={12} className="text-gold"/> {t('learn.recommended_for_you')}
            </p>
            <Link
              to={`/app/learn/lesson/${l.id}`}
              data-testid="recommended-top-card"
              className="relative block overflow-hidden rounded-3xl card-soft text-white hover:-translate-y-0.5 transition-smooth min-h-[220px] lg:min-h-[260px]">
              {cover && (
                <>
                  <img src={cover} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-navy via-navy/75 to-navy/30" />
                </>
              )}
              <div className={`relative z-10 flex flex-col h-full p-6 lg:p-8 ${cover ? '' : 'bg-navy'}`}>
                {l.recommended_reason && (
                  <span
                    data-testid="recommended-reason-chip"
                    className="self-start inline-flex items-center gap-1.5 text-xs lg:text-sm font-semibold bg-gold text-navy rounded-full px-3.5 py-1.5 mb-4">
                    <Sparkles size={13} strokeWidth={2}/> {l.recommended_reason}
                  </span>
                )}
                <div className="w-11 h-11 lg:w-12 lg:h-12 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center mb-3">
                  <Icon size={20} className="text-white" strokeWidth={1.5}/>
                </div>
                <p className="text-[10px] lg:text-xs uppercase tracking-widest text-white/70">{cat?.name || l.category}</p>
                <p className="font-heading text-xl lg:text-3xl mt-1 leading-snug max-w-2xl">{l.title}</p>
                {l.summary && <p className="text-sm lg:text-base text-white/80 mt-2 leading-relaxed line-clamp-2 max-w-xl">{l.summary}</p>}
                <p className="text-[11px] lg:text-xs text-white/60 uppercase tracking-widest mt-4">{l.duration} · Start lesson ↗</p>
              </div>
            </Link>
          </div>
        );
      })()}

      {/* Search + filter toolbar — kept near the top, above the course list */}
      <div className="sticky top-16 z-30 -mx-6 px-6 lg:mx-0 lg:px-0 py-3 bg-[#F9F8F6]/95 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40"/>
            <input
              value={query}
              onChange={e=>setQuery(e.target.value)}
              data-testid="learn-search"
              placeholder="Search lessons — anxiety, money, fatherhood…"
              className="w-full bg-white border border-navy/10 rounded-full pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-gold text-sm lg:text-base"
            />
          </div>
          <select
            value={sortBy}
            onChange={e=>setSortBy(e.target.value)}
            data-testid="learn-sort"
            className="hidden lg:block bg-white border border-navy/10 rounded-full px-4 py-3 outline-none focus:ring-2 focus:ring-gold text-sm">
            <option value="Recommended">Sort: Recommended</option>
            <option value="Newest">Sort: Newest</option>
            <option value="Most Popular">Sort: Most Popular</option>
            <option value="A–Z">Sort: A–Z</option>
          </select>
          <button
            onClick={()=>setShowFilters(!showFilters)}
            data-testid="learn-filter-toggle"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm lg:text-base ${showFilters ? 'bg-navy text-white' : 'bg-white border border-navy/10 text-navy'}`}>
            <SlidersHorizontal size={14}/> Filters
          </button>
        </div>
        {active !== 'all' && (
          <button onClick={()=>{ setActive('all'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            data-testid="learn-all-courses"
            className="mt-3 inline-flex items-center gap-1.5 text-xs lg:text-sm text-navy/60 hover:text-navy transition-smooth">
            <ArrowRight size={13} className="rotate-180"/> All courses
          </button>
        )}
      </div>

      {/* Saved courses — pinned row, only when the user has saved courses (and not searching) */}
      {active === 'all' && !query.trim() && (() => {
        const savedList = cats
          .filter(c => c.id !== 'support' && savedCourseIds.has(c.id))
          .filter(c => lessons.some(l => l.category === c.id));
        if (savedList.length === 0) return null;
        return (
          <div data-testid="saved-courses-section">
            <p className="text-xs uppercase tracking-[0.2em] text-navy/50 font-semibold mb-3 lg:mb-5 inline-flex items-center gap-2">
              <Bookmark size={12} className="text-gold fill-gold"/> Saved courses ({savedList.length})
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5" data-testid="saved-courses-grid">
              {savedList.map(c => {
                const Icon = ICONS[c.icon] || CATEGORY_ICON[c.id] || Sparkles;
                return (
                  <CourseTile key={c.id} c={c} lessons={lessons} completedIds={completedIds} Icon={Icon}
                    saved={true} onToggleSave={toggleCourseFav}
                    onOpen={() => { setActive(c.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Courses — shown in the default view AND filtered live while searching */}
      {active === 'all' && (() => {
        const q = query.trim().toLowerCase();
        const courseList = cats
          .filter(c => c.id !== 'support')
          .filter(c => lessons.some(l => l.category === c.id))
          .filter(c => !savedCoursesOnly || savedCourseIds.has(c.id))
          .filter(c => !q || (c.name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
        if ((q || savedCoursesOnly) && courseList.length === 0) {
          return (
            <div className="bg-white rounded-3xl p-8 lg:p-10 card-soft text-center" data-testid="courses-empty">
              <p className="font-heading text-lg">{savedCoursesOnly ? 'No saved courses yet' : 'No courses match'}</p>
              <p className="text-sm text-navy/60 mt-2">{savedCoursesOnly ? 'Tap the bookmark on any course to save it here.' : 'Try a different search.'}</p>
            </div>
          );
        }
        return (
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-navy/50 font-semibold mb-3 lg:mb-5">{q ? `Matching courses (${courseList.length})` : (savedCoursesOnly ? `Saved courses (${courseList.length})` : 'Courses')}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5" data-testid="courses-grid">
          {courseList.map(c => {
            const Icon = ICONS[c.icon] || CATEGORY_ICON[c.id] || Sparkles;
            if (!lessons.some(l => l.category === c.id)) return null;
            return (
              <CourseTile key={c.id} c={c} lessons={lessons} completedIds={completedIds} Icon={Icon}
                saved={savedCourseIds.has(c.id)} onToggleSave={toggleCourseFav}
                onOpen={() => { setActive(c.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            );
          })}
        </div>
      </div>
        );
      })()}

      {/* Filter panel (collapsible) */}
      {showFilters && (
        <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft space-y-6" data-testid="learn-filter-panel">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-3">Difficulty</p>
              <div className="flex flex-wrap gap-2">
                {['all','Beginner','Intermediate','Advanced'].map(d => (
                  <button key={d} onClick={()=>setDifficultyFilter(d)} data-testid={`f-diff-${d}`}
                    className={`text-xs lg:text-sm px-4 py-2 rounded-full ${difficultyFilter===d ? 'bg-navy text-white' : 'bg-beige text-navy'}`}>
                    {d === 'all' ? 'Any' : d}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-3">Length</p>
              <div className="flex flex-wrap gap-2">
                {[{v:'all',l:'Any'},{v:'short',l:'≤ 5 min'},{v:'medium',l:'6–8 min'},{v:'long',l:'9+ min'}].map(o => (
                  <button key={o.v} onClick={()=>setLengthFilter(o.v)} data-testid={`f-len-${o.v}`}
                    className={`text-xs lg:text-sm px-4 py-2 rounded-full ${lengthFilter===o.v ? 'bg-navy text-white' : 'bg-beige text-navy'}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-3">Progress</p>
              <div className="flex flex-wrap gap-2">
                {[{v:'all',l:'Any', I: null},
                  {v:'not_started',l:'Not started', I: Circle},
                  {v:'in_progress',l:'In progress', I: PlayCircle},
                  {v:'completed',l:'Completed', I: CheckCircle2}].map(o => {
                  const I = o.I;
                  return (
                    <button key={o.v} onClick={()=>setStatusFilter(o.v)} data-testid={`f-status-${o.v}`}
                      className={`text-xs lg:text-sm px-4 py-2 rounded-full inline-flex items-center gap-1 ${statusFilter===o.v ? 'bg-navy text-white' : 'bg-beige text-navy'}`}>
                      {I && <I size={12}/>}{o.l}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-3">Mood / Need</p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(MOOD_MAP).map(m => (
                <button key={m} onClick={()=>toggleMood(m)} data-testid={`f-mood-${m}`}
                  className={`text-xs lg:text-sm px-4 py-2 rounded-full ${moodFilters.includes(m) ? 'bg-gold text-white' : 'bg-beige text-navy'}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-navy/5">
            <button onClick={()=>setFavoritesOnly(!favoritesOnly)} data-testid="f-favorites-only"
              className={`inline-flex items-center gap-1 text-xs lg:text-sm px-4 py-2 rounded-full ${favoritesOnly ? 'bg-gold text-white' : 'bg-beige text-navy'}`}>
              <Star size={12} className={favoritesOnly ? 'fill-white' : ''}/> Saved lessons only
            </button>
            <button onClick={()=>{ setSavedCoursesOnly(!savedCoursesOnly); setActive('all'); }} data-testid="f-saved-courses-only"
              className={`inline-flex items-center gap-1 text-xs lg:text-sm px-4 py-2 rounded-full ${savedCoursesOnly ? 'bg-gold text-white' : 'bg-beige text-navy'}`}>
              <Bookmark size={12} className={savedCoursesOnly ? 'fill-white' : ''}/> Saved courses only
            </button>
            <div className="lg:hidden">
              <select value={sortBy} onChange={e=>setSortBy(e.target.value)} data-testid="learn-sort-mobile"
                className="text-xs bg-beige rounded-full px-4 py-2 outline-none">
                <option value="Recommended">Sort: Recommended</option>
                <option value="Newest">Sort: Newest</option>
                <option value="Most Popular">Sort: Most Popular</option>
                <option value="A–Z">Sort: A–Z</option>
              </select>
            </div>
            <button onClick={()=>{setDifficultyFilter('all');setLengthFilter('all');setStatusFilter('all');setMoodFilters([]);setFavoritesOnly(false);setSavedCoursesOnly(false);setQuery('');setSortBy('Recommended');}}
              className="ml-auto text-xs lg:text-sm text-navy/50 hover:text-navy underline underline-offset-4">
              Reset all filters
            </button>
          </div>
        </div>
      )}

      {/* Category hero banner — cinematic entrance when a course is selected */}
      {active !== 'all' && (() => {
        const activeCat = cats.find(c => c.id === active);
        const fc = FEATURED_COURSES.find(f => f.key === active);
        const heroCover = activeCat?.cover_image;
        return (
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-testid="category-hero"
            className="relative overflow-hidden rounded-3xl h-[260px] lg:h-[300px] card-soft bg-navy">
            {heroCover ? (
              <>
                {/* blurred fill so the full photo shows without cropping the subject */}
                <img src={heroCover} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-50" />
                <img src={heroCover} alt="" className="absolute inset-0 w-full h-full object-contain" />
              </>
            ) : (
              <div className="absolute inset-0 bg-navy" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/45 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy/70 via-transparent to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-6 lg:p-9">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">Category</p>
              <h2 className="font-heading text-white text-3xl lg:text-4xl mt-1 leading-tight">{activeCat?.name || fc?.title}</h2>
              {(activeCat?.description || fc?.blurb) && <p className="text-white/75 text-sm lg:text-base mt-2 max-w-xl">{activeCat?.description || fc?.blurb}</p>}
            </div>
          </motion.div>
        );
      })()}

      {/* Result count */}
      {(active !== 'all' || query.trim()) && (
      <div className="flex items-center justify-between">
        <p className="text-xs lg:text-sm text-navy/50">
          Showing {filtered.length} of {lessons.length} lessons
        </p>
      </div>
      )}

      {/* Lessons — flat results when searching; grouped learning paths when a course is selected */}
      {active === 'all' ? (
        query.trim() ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5" data-testid="lessons-grid">
          {filtered.map(l => {
            const cat = cats.find(c => c.id === l.category);
            const Icon = ICONS[cat?.icon] || CATEGORY_ICON[l.category] || Sparkles;
            return (
              <LessonCard key={l.id} l={l} cat={cat} Icon={Icon}
                completed={completedIds.has(l.id)}
                inProgress={!!progressMap[l.id] && !completedIds.has(l.id)}
                favorited={favoriteIds.has(l.id)}
                onFav={toggleFav} />
            );
          })}
        </div>
        ) : null
      ) : (
        <div className="space-y-8 lg:space-y-12" data-testid="lessons-grid">
          {[1, 2, 3].map(tier => {
            const tierLessons = filtered
              .filter(l => (l.path || 1) === tier)
              .sort((a, b) => (a.order || 0) - (b.order || 0));
            if (tierLessons.length === 0) return null;
            const meta = PATH_TIERS[tier];
            const tierDone = tierLessons.filter(l => completedIds.has(l.id)).length;
            const tierPct = tierLessons.length ? Math.round((tierDone / tierLessons.length) * 100) : 0;
            return (
              <div key={tier} data-testid={`learning-path-${tier}`}>
                <div className="flex items-center gap-3 mb-4 lg:mb-6">
                  <PathRing tier={tier} pct={tierPct} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] lg:text-sm uppercase tracking-[0.2em] text-navy font-bold">Path {tier} · {meta.title}</p>
                    <p className="text-xs lg:text-sm text-navy/70">{meta.blurb}</p>
                    <p className="text-[11px] lg:text-xs text-navy/60 font-semibold mt-0.5" data-testid={`path-progress-${tier}`}>
                      {tierDone} of {tierLessons.length} done{tierPct === 100 ? ' · path complete' : ''}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
                  {tierLessons.map(l => {
                    const cat = cats.find(c => c.id === l.category);
                    const Icon = ICONS[cat?.icon] || CATEGORY_ICON[l.category] || Sparkles;
                    return (
                      <LessonCard key={l.id} l={l} cat={cat} Icon={Icon}
                        completed={completedIds.has(l.id)}
                        inProgress={!!progressMap[l.id] && !completedIds.has(l.id)}
                        favorited={favoriteIds.has(l.id)}
                        onFav={toggleFav} />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {(active !== 'all' || query.trim()) && filtered.length === 0 && (
        <div className="bg-white rounded-3xl p-10 lg:p-14 card-soft text-center">
          <p className="font-heading text-xl">No lessons match your filters</p>
          <p className="text-sm text-navy/60 mt-2">Try clearing a mood chip or the search query.</p>
        </div>
      )}

      {/* Path Complete celebration + shareable badge */}
      {celebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm animate-fade-in" data-testid="path-complete-modal" onClick={() => setCelebration(null)}>
          <div className="relative overflow-hidden bg-white rounded-3xl p-8 lg:p-10 max-w-sm w-full text-center card-soft" onClick={e => e.stopPropagation()}>
            <span aria-hidden="true" className="pointer-events-none select-none absolute -bottom-10 -right-6 font-heading font-extrabold leading-none text-navy/[0.05]" style={{ fontSize: '190px' }}>B<span className="text-gold/20">+</span></span>
            <button data-testid="path-complete-close" onClick={() => setCelebration(null)} className="absolute top-4 right-4 z-10 text-navy/40 hover:text-navy transition-smooth"><X size={20}/></button>
            <div className="relative z-10">
            <div className="mx-auto w-20 h-20 rounded-full bg-gold/15 flex items-center justify-center mb-5">
              <div className="w-14 h-14 rounded-full bg-gold flex items-center justify-center">
                <Trophy size={26} className="text-navy" strokeWidth={1.75}/>
              </div>
            </div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">Path {celebration.tier} Complete</p>
            <h3 className="font-heading text-2xl lg:text-3xl mt-2 text-navy bh-goldshine">{celebration.label}</h3>
            <p className="text-sm text-navy/60 mt-2">You finished every lesson in the <span className="font-semibold text-navy">{celebration.label}</span> path of <span className="font-semibold text-navy">{celebration.courseName}</span>. That&apos;s real work, brother.</p>
            <div className="mt-6 flex flex-col gap-2">
              <button data-testid="path-complete-share" onClick={shareBadge} className="w-full py-3 rounded-full bg-navy text-white font-semibold inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-smooth"><Share2 size={16}/> Share your badge</button>
              <button data-testid="path-complete-keep-going" onClick={() => setCelebration(null)} className="w-full py-3 rounded-full bg-beige text-navy font-semibold hover:bg-[#EAE5D9] transition-smooth">Keep going</button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
